/**
 * Token Acquisition Utilities
 * 
 * Centralized token acquisition with request deduplication.
 * Prevents duplicate acquireTokenSilent() calls when multiple requests
 * happen concurrently.
 * 
 * Benefits:
 * - Prevents MSAL race conditions
 * - Reduces unnecessary token requests
 * - Improves performance under load
 * - More stable concurrent requests
 */

import { AccountInfo, SilentRequest, AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser';
import { getAuthScopes } from '../config/msal.config';

/**
 * In-flight request cache
 * Maps request key to the promise for that request
 */
const inFlightRequests = new Map<string, Promise<AuthenticationResult>>();

/**
 * Generate a unique key for a token request
 * Same account + scopes = same key = same request
 */
function getRequestKey(account: AccountInfo, scopes: string[]): string {
  const sortedScopes = [...scopes].sort().join(',');
  return `${account.homeAccountId}:${sortedScopes}`;
}

/**
 * Acquire token silently with request deduplication
 * 
 * If a request for the same account + scopes is already in flight,
 * returns the existing promise instead of making a new request.
 * 
 * @param msalInstance - MSAL instance
 * @param account - Account to acquire token for
 * @param scopes - Optional scopes (defaults to getAuthScopes())
 * @returns Promise resolving to authentication result
 */
export async function acquireTokenSilentDeduplicated(
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
  scopes?: string[]
): Promise<AuthenticationResult> {
  const requestScopes = scopes ?? getAuthScopes();
  const requestKey = getRequestKey(account, requestScopes);
  
  // Check if request is already in flight
  const existingRequest = inFlightRequests.get(requestKey);
  if (existingRequest) {
    if (import.meta.env.DEV) {
      console.log('[Token Acquisition] Reusing in-flight request for:', requestKey);
    }
    return existingRequest;
  }
  
  // Create new request
  const request: SilentRequest = {
    scopes: requestScopes,
    account: account,
  };
  
  if (import.meta.env.DEV) {
    console.log('[Token Acquisition] Creating new request for:', requestKey);
  }
  
  // Create promise and store it
  const requestPromise = msalInstance.acquireTokenSilent(request)
    .then((result) => {
      // Request completed - remove from cache
      inFlightRequests.delete(requestKey);
      if (import.meta.env.DEV) {
        console.log('[Token Acquisition] Request completed for:', requestKey);
      }
      return result;
    })
    .catch((error) => {
      // Request failed - remove from cache
      inFlightRequests.delete(requestKey);
      if (import.meta.env.DEV) {
        console.log('[Token Acquisition] Request failed for:', requestKey, error);
      }
      throw error;
    });
  
  // Store in-flight request
  inFlightRequests.set(requestKey, requestPromise);
  
  return requestPromise;
}

/**
 * Clear all in-flight requests (useful for testing or cleanup)
 */
export function clearInFlightRequests(): void {
  inFlightRequests.clear();
  if (import.meta.env.DEV) {
    console.log('[Token Acquisition] Cleared all in-flight requests');
  }
}

/**
 * Get count of in-flight requests (useful for debugging)
 */
export function getInFlightRequestCount(): number {
  return inFlightRequests.size;
}

/**
 * Get in-flight request keys (useful for debugging)
 */
export function getInFlightRequestKeys(): string[] {
  return Array.from(inFlightRequests.keys());
}
